import React, { useEffect, useCallback } from 'react';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';
import { AnimatePresence, motion } from 'framer-motion';

/**
 * Universal Lightbox with prev/next chevrons, keyboard shortcuts,
 * counter, caption and smooth crossfade transitions (Framer Motion).
 *
 * Props:
 *  - images: array of { image, titleKey, location? }
 *  - index: current image index
 *  - onClose: () => void
 *  - onIndexChange: (idx) => void
 *  - subtitle: optional string above title (e.g. "Plattenleger · Seelisberg")
 */
const Lightbox = ({ images, index, onClose, onIndexChange, subtitle }) => {
  const total = images?.length || 0;
  const current = images?.[index];

  const goPrev = useCallback((e) => {
    if (e && e.stopPropagation) e.stopPropagation();
    onIndexChange((index - 1 + total) % total);
  }, [index, total, onIndexChange]);

  const goNext = useCallback((e) => {
    if (e && e.stopPropagation) e.stopPropagation();
    onIndexChange((index + 1) % total);
  }, [index, total, onIndexChange]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft') goPrev();
      if (e.key === 'ArrowRight') goNext();
    };
    window.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [onClose, goPrev, goNext]);

  if (!current) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.25 }}
      onClick={onClose}
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-brand-ink/95 backdrop-blur-md"
      data-testid="lightbox-container"
    >
      {/* Close */}
      <button
        onClick={(e) => { e.stopPropagation(); onClose(); }}
        aria-label="Schliessen"
        data-testid="lightbox-close"
        className="absolute top-6 right-6 w-14 h-14 rounded-full bg-white/10 hover:bg-brand-blue border border-white/20 text-white flex items-center justify-center transition-colors z-10"
      >
        <X className="w-6 h-6" />
      </button>

      {/* Counter */}
      <div
        className="absolute top-8 left-1/2 -translate-x-1/2 text-white/90 text-xs md:text-sm font-bold tracking-[0.4em] uppercase z-10 bg-white/10 border border-white/20 px-4 py-1.5 rounded-full backdrop-blur"
        data-testid="lightbox-counter"
      >
        {String(index + 1).padStart(2, '0')} / {String(total).padStart(2, '0')}
      </div>

      {/* Prev */}
      {total > 1 && (
        <button
          onClick={goPrev}
          aria-label="Vorheriges Bild"
          data-testid="lightbox-prev"
          className="absolute left-4 md:left-8 top-1/2 -translate-y-1/2 w-14 h-14 md:w-16 md:h-16 rounded-full bg-white/10 hover:bg-brand-blue border border-white/20 text-white flex items-center justify-center transition-all hover:scale-110 z-10"
        >
          <ChevronLeft className="w-7 h-7" />
        </button>
      )}

      {/* Next */}
      {total > 1 && (
        <button
          onClick={goNext}
          aria-label="Nächstes Bild"
          data-testid="lightbox-next"
          className="absolute right-4 md:right-8 top-1/2 -translate-y-1/2 w-14 h-14 md:w-16 md:h-16 rounded-full bg-white/10 hover:bg-brand-blue border border-white/20 text-white flex items-center justify-center transition-all hover:scale-110 z-10"
        >
          <ChevronRight className="w-7 h-7" />
        </button>
      )}

      {/* Image + caption */}
      <div className="max-w-6xl w-full flex flex-col items-center gap-5" onClick={(e) => e.stopPropagation()}>
        <AnimatePresence mode="wait">
          <motion.img
            key={current.image}
            src={current.image}
            alt={current.titleKey}
            initial={{ opacity: 0, scale: 0.98 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 1.02 }}
            transition={{ duration: 0.35, ease: [0.2, 0.7, 0.2, 1] }}
            className="max-w-full max-h-[75vh] object-contain rounded-md shadow-2xl"
            data-testid="lightbox-image"
          />
        </AnimatePresence>
        <div className="text-center px-4">
          {subtitle && (
            <div className="text-brand-blueSky text-xs uppercase tracking-[0.3em] mb-2 font-bold">{subtitle}</div>
          )}
          <h3 className="font-display text-2xl md:text-3xl text-white mb-1">{current.titleKey}</h3>
          {current.location && (
            <div className="text-white/60 text-sm">{current.location}</div>
          )}
        </div>
      </div>
    </motion.div>
  );
};

export default Lightbox;
